import { ApolloError } from '@apollo/client';
import { AxiosError } from 'axios';
import { GraphQLError } from 'graphql';
import { TMessage, TUserInputRequirement, TAppDebugInfo, TStatusMessage, TLayout, TFont, TPalette, TIcon, IWebInteractionOutcome, TaiPluginRunInfo, TAppBreakpoint, TDatasetDisplay } from '../../generated/gql/graphql';
import { JSONSchema7 } from 'json-schema';

export type AppRunError = AxiosError<any, any> | ApolloError | GraphQLError | { name: string, message: string };

export type InterruptedReason = 'error' | 'stop';


export type CanvasDisplaySelection = {
  type: 'dataset',
  name: string,
} | {
  type: 'debug',
}

export type ChildAppClientState = {
  appName: string | null;
  clientId: string;
  // only when the app is inside extension, otherwise it is undefined
  tabId: number | undefined;
  flowId: string;
  unreadCount: number;
  serverInProgress: boolean;
  completed: boolean;
  interrupted: InterruptedReason | null;
  terminated: boolean;
};

export const DEFAULT_CHILD_APP_STATE = {
  appName: null,
  clientId: '',
  tabId: undefined,
  flowId: '',
  unreadCount: 0,
  serverInProgress: false,
  completed: false,
  interrupted: null,
  terminated: false,
}

export type AppRunCommand = {
  type: 'rerun',
  nodeId: string,
  nodeVersion: number,
}

export type ClientState = {
  appName: string | null;
  clientId: string | undefined;
  flowId: string;
  inConversation: boolean;
  unreadCount: number;
  // App run state
  initializing: boolean;
  serverInProgress: boolean;
  inputRequirement: TUserInputRequirement | undefined;
  chatEntries: (TMessage | TStatusMessage | AppRunError)[];
  debugLogs: TAppDebugInfo[];
  aiPluginRunLogs: TaiPluginRunInfo[];
  completed: boolean;
  // whether the styling has been loaded from server
  // This will be true even if the request failed.
  stylingLoaded: boolean;
  _palatte: TPalette | undefined;
  _font: TFont | undefined;
  _layout: TLayout | undefined;
  icon: TIcon | null;
  openAfter: number | null;
  interrupted: InterruptedReason | null;
  // outcome of the last web interaction
  webInteractionOutcome: IWebInteractionOutcome;
  aiLabels: { nodeId: string, nodeVersion: number, label: boolean }[];
  datasets: { [datasetName: string]: TDatasetDisplay };
  canvasDisplay: CanvasDisplaySelection | null;
  lastChildAppClientId: string | null;
  childApps: { [clientId: string]: ChildAppClientState };

  // set app run command to trigger app run actions
  appRunCommand: AppRunCommand | null;
  profilePicUrl: string | null; // Add profilePicUrl to the client state
};

export type StylingWithDefaults = {
  palette: TPalette;
  font: TFont;
  layout: TLayout;
  openAfter: number | null;
  icon: TIcon | null;
  profilePicUrl: string | null; // Add profilePicUrl to the styling defaults
};
